import type {
    StationLocation,
    UpdateStationLocationRequest,
} from '../../api/station/stationApiTypes';
import type { OpeningHourEntry } from '../../types/locations';
import { type Coordinates, coordinatesState } from '../../utils/coordinates';
import { toStationOpeningHours } from './createStation/stationCreationPayload';

/**
 * What the station/house page edits about its LOCATION while "Bearbeiten" is
 * open — one draft for the whole page, saved by the page's one "Speichern"
 * (PO, 2026-10-19; `docs/design-decisions.md` › Bearbeiten und Speichern).
 * Pure, so what a save sends is testable without the page.
 */
export interface LocationDraft {
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    district: string;
    pin: Coordinates;
    timeZone: string;
    hours: OpeningHourEntry[];
    status: { enabled: boolean, bookable: boolean } | null;
    /** A picked photo, uploaded on save. */
    photoFile: File | null;
    /** The shown photo is dropped on save. */
    removePhoto: boolean;
}

export const hoursOf = (location: StationLocation | null): OpeningHourEntry[] =>
    (location?.openingHours ?? [])
        .filter((hour) => typeof hour.weekday === 'string')
        .map((hour) => ({
            weekday: hour.weekday as OpeningHourEntry['weekday'],
            times: { fromHour: hour.fromHour, toHour: hour.toHour },
        }));

export const locationDraftFrom = (location: StationLocation | null): LocationDraft => ({
    street: location?.address?.street ?? '',
    houseNumber: location?.address?.houseNumber ?? '',
    postalCode: location?.address?.postalCode ?? '',
    city: location?.address?.city ?? '',
    district: location?.address?.district ?? '',
    pin: { geoLat: location?.address?.geoLat ?? null, geoLng: location?.address?.geoLng ?? null },
    timeZone: location?.timeZone ?? '',
    hours: hoursOf(location),
    status: location?.status ?? null,
    photoFile: null,
    removePhoto: false,
});

const sameHours = (a: OpeningHourEntry[], b: OpeningHourEntry[]): boolean =>
    JSON.stringify(toStationOpeningHours(a)) === JSON.stringify(toStationOpeningHours(b));

/**
 * The location route's body for what changed — `null` when nothing did. Only
 * a change is sent: an untouched zone must not rewrite its stored spelling, an
 * untouched pin or district must not be re-written, and where the address is
 * fixed no `address` is sent at all (the server refuses one).
 */
export const locationPatchOf = (
    draft: LocationDraft,
    location: StationLocation | null,
    addressFixed: boolean,
): UpdateStationLocationRequest | null => {
    const saved = locationDraftFrom(location);
    const patch: UpdateStationLocationRequest = {};
    const addressChanged = ['street', 'houseNumber', 'postalCode', 'city']
        .some((key) => draft[key as 'street'].trim() !== saved[key as 'street'].trim());
    if (!addressFixed && addressChanged) {
        patch.address = {
            street: draft.street.trim(),
            houseNumber: draft.houseNumber.trim(),
            postalCode: draft.postalCode.trim(),
            city: draft.city.trim(),
        };
    }
    if (draft.district.trim() !== saved.district.trim()) patch.district = draft.district.trim();
    if (draft.pin.geoLat !== saved.pin.geoLat || draft.pin.geoLng !== saved.pin.geoLng) {
        patch.coordinates = coordinatesState(draft.pin) === 'complete'
            ? { geoLat: draft.pin.geoLat as number, geoLng: draft.pin.geoLng as number }
            : null;
    }
    if (draft.timeZone && draft.timeZone !== saved.timeZone) patch.timeZone = draft.timeZone;
    if (!sameHours(draft.hours, saved.hours)) patch.openingHours = toStationOpeningHours(draft.hours);
    return Object.keys(patch).length > 0 ? patch : null;
};

/** Whether anything in the location draft differs from what is stored. */
export const locationDraftChanged = (
    draft: LocationDraft, location: StationLocation | null, addressFixed: boolean,
): boolean => locationPatchOf(draft, location, addressFixed) !== null
    || draft.photoFile !== null
    || draft.removePhoto
    || (draft.status !== null && location?.status != null
        && (draft.status.enabled !== location.status.enabled
            || draft.status.bookable !== location.status.bookable));
